// RIBBON FLYOUT — the ONE primitive behind every multi-option Row-1 control
// (R2.5 flyout redesign, 2026-07-20; owner directive verbatim: "a small chip
// on the bottom right of the … button will suffice to indicate there is a
// flyout available").
//
// Anatomy:
//   • FACE — a house-sized icon button (cce-tb-btn) carrying the control's
//     current state on its own face (current weight lit bold, current case
//     highlighted, current color as the swatch) + a tooltip (educator-plain
//     label — hover tells you what it does);
//   • CORNER CHIP — the small bottom-right triangle that says "this opens a
//     flyout". Decorative (a span) when the face itself opens the panel;
//     a REAL 12px button when the face has a fast path (Bold toggles on
//     click, its chip opens the weights flyout) — both render .rbn-fly-chip
//     so probes can census "chips on exactly the flyout-bearing buttons";
//   • PANEL — a popover under the button (shared .rbn-menu skin: one shadow
//     token, one radius token). Closes on Esc (focus returns to the face),
//     on outside mousedown, and on apply where the choice is terminal
//     (one-of pickers close; sliders/color labs stay open for multi-adjust
//     — the house "navigation closes, adjustment stays" law). Never clips:
//     a layout effect clamps the panel back inside the viewport.
//
// Keyboard: face + chip are real buttons (Tab reachable); panel controls are
// native buttons/inputs; Esc anywhere inside closes. The primitive owns NO
// state semantics — faces/panels arrive as props, handlers stay outside.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export default function RibbonFlyout({
  flyId,                 // stable id → data-fly-id on the anchor, data-fly-for on the panel
  label,                 // aria-label + tooltip for the face
  face,                  // node rendered inside the face button
  active = false,        // face active state (current-state rendering)
  onFaceClick,           // FAST PATH: when given, the face fires this and ONLY the chip opens
  panel,                 // node OR ({ close }) => node — the flyout body
  panelClassName = '',
  openOnFocus = false,   // size-field mode: focusing the face opens the panel
  faceClassName = '',
  faceAs,                // custom face renderer: ({ open, props }) => node (size field)
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const faceRef = useRef(null);
  const panelRef = useRef(null);
  const [shiftX, setShiftX] = useState(0);

  const close = (refocus = false) => {
    setOpen(false);
    if (refocus) faceRef.current?.focus?.();
  };

  // outside mousedown closes (house popover behavior)
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (ev) => {
      if (rootRef.current && !rootRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // Esc closes and hands focus back to the face (keyboard law).
  // Layer order: the house paint MODAL (.cce-paintmodal — owner 2026-07-21) may
  // be open OVER this flyout (e.g. an effect-param border-color chip opened it).
  // Esc there must pop the MODAL only (it owns the shared Esc stack), so events
  // originating inside it are ignored here — otherwise this flyout would eat the
  // Esc and the top-most modal would never close. This is the matching Esc half.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'Escape') {
        if (ev.target?.closest?.('.cce-paintmodal')) return;
        ev.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  // never clipped: clamp the panel back inside the viewport (right edge)
  useLayoutEffect(() => {
    if (!open) { setShiftX(0); return; }
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const overflow = rect.right - (window.innerWidth - 8);
    if (overflow > 0) setShiftX(-Math.ceil(overflow));
    else if (rect.left < 8) setShiftX(Math.ceil(8 - rect.left));
  }, [open]);

  const split = typeof onFaceClick === 'function';
  const chip = <span className="rbn-fly-chip" aria-hidden="true" />;

  const faceProps = {
    ref: faceRef,
    type: 'button',
    className: `cce-tb-btn rbn-fly-btn${active ? ' active' : ''}${faceClassName ? ` ${faceClassName}` : ''}`,
    title: label,
    'aria-label': label,
    'aria-haspopup': split ? undefined : 'true',
    'aria-expanded': split ? undefined : open,
    onClick: split ? () => onFaceClick() : () => setOpen((o) => !o),
    onFocus: openOnFocus ? () => setOpen(true) : undefined,
  };

  return (
    <span className={`rbn-anchor rbn-fly${split ? ' rbn-fly-split' : ''}`} data-fly-id={flyId} ref={rootRef}>
      {faceAs
        ? faceAs({ open, setOpen, faceRef })
        : (
          <button {...faceProps}>
            {face}
            {!split && chip}
          </button>
        )}
      {split && (
        <button
          type="button"
          className="rbn-fly-chipbtn"
          title={`${label} — more choices`}
          aria-label={`${label} — more choices`}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {chip}
        </button>
      )}
      {open && (
        <div
          className={`rbn-menu rbn-fly-panel${panelClassName ? ` ${panelClassName}` : ''}`}
          data-fly-for={flyId}
          role="dialog"
          aria-label={label}
          ref={panelRef}
          style={shiftX ? { transform: `translateX(${shiftX}px)` } : undefined}
        >
          {typeof panel === 'function' ? panel({ close }) : panel}
        </div>
      )}
    </span>
  );
}

/** Shared flyout option row — one-of choices (weights, case, lists, align,
 *  curve). Terminal picks: onPick fires, then the panel closes. `current`
 *  renders the ✓ + active skin; data-opt carries the ENGINE value for
 *  probes/suites. */
export function FlyoutOption({ value, label, current = false, onPick, icon = null, sample = null }) {
  return (
    <button
      type="button"
      className={`rbn-fly-opt${current ? ' active' : ''}`}
      role="menuitemradio"
      aria-checked={current}
      aria-label={label}
      title={label}
      data-opt={String(value)}
      onClick={onPick}
    >
      {icon}
      {sample != null && <span className="rbn-fly-sample" aria-hidden="true">{sample}</span>}
      <span className="rbn-fly-opt-label">{label}</span>
      {current && <span className="rbn-fly-check" aria-hidden="true">✓</span>}
    </button>
  );
}

/** The drawer link as a flyout footer — the group's "More options…" lives
 *  INSIDE its flyout now (owner: no bare "…" corners on the text row). */
export function FlyoutFooter({ drawerSection, onOpenDrawer, close }) {
  if (!drawerSection || !onOpenDrawer) return null;
  return (
    <button
      type="button"
      className="rbn-menu-footer"
      data-drawer-section={drawerSection}
      onClick={() => { onOpenDrawer(drawerSection); close?.(); }}
    >
      More options…
    </button>
  );
}
